import { Box, Skeleton } from "@mui/material";
import React from "react";
import RefreshIcon from "@mui/icons-material/Refresh";
import useTheme from "../../../../../common/hooks/useTheme";
import ActionButton from "../../../../../common/ui/ActionButton";
import { Theme } from "../../../../../providers/ThemeProvider";
import InfoIcon from "../../../svg/InfoIcon";
import CustomTooltip from "../../common/CustomTooltip";
import InlineBanner from "../../common/InlineBanner";
import PeriodNavigator from "../../common/PeriodNavigator";
import { formatWeekLabel, thisWeekMonday } from "../shared/planDates";
import WorkoutAnalysisPanel from "./components/analysis/WorkoutAnalysisPanel";
import DetailsPane from "./components/details/DetailsPane";
import WorkoutPlanSkeleton from "./components/shared/WorkoutPlanSkeleton";
import WeekPlanView from "./components/weekly/WeekPlanView";
import { weekCompletion } from "./helpers/activitySelectors";
import { useWorkoutPlan } from "./store/useWorkoutPlan";

const REFRESH_HINT =
  "Logged or moved a workout in the Precision app? Tap refresh and we'll catch up.";

/**
 * The plan page under Programming → Workout: title, the week navigator and
 * a "Refresh data" action (the week is fetched once and cached, so changes
 * made in the Precision app appear only after a refresh), the week's
 * completion count, then the week's days (`WeekPlanView`). Loading, "no
 * plan for this week" and error states are handled here so the view only
 * renders data. The body is split: the week's days on the left, the selected
 * entry's details on the right (`DetailsPane`); a recorded session's
 * analysis opens in the side panel (`WorkoutAnalysisPanel`).
 */
const WorkoutPlanView = () => {
  const theme: Theme = useTheme();
  const {
    weekStartDate,
    activeWeek,
    activeWeekStatus,
    goToPreviousWeek,
    goToNextWeek,
    goToThisWeek,
    reload,
  } = useWorkoutPlan();

  const completion =
    activeWeekStatus === "ready" && activeWeek ? weekCompletion(activeWeek) : null;

  const renderBody = () => {
    if (activeWeekStatus === "ready") return <WeekPlanView />;
    if (activeWeekStatus === "error") {
      return (
        <InlineBanner
          text="Couldn't load the workout plan."
          backgroundColor={theme.colorTokens.D[10]}
          iconColor={theme.colorTokens.D[70]}
          action={{ label: "Retry", onClick: reload }}
        />
      );
    }
    if (activeWeekStatus === "empty") {
      return (
        <InlineBanner
          text="No plan for this week."
          backgroundColor={theme.colorTokens.P[10]}
          iconColor={theme.colors_ds.PRIMARY_ICON_MAIN}
        />
      );
    }
    return <WorkoutPlanSkeleton />;
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.headerRow}>
        <Box sx={styles.titleGroup}>
          <Box sx={styles.title(theme)}>Workout Plan</Box>
          {/* Always one line tall, so the header does not change height (and
              the body jump) between the skeleton and the loaded week. */}
          <Box sx={styles.completion(theme)}>
            {activeWeekStatus === "loading" || activeWeekStatus === "idle" ? (
              <Skeleton
                variant="rounded"
                animation="pulse"
                sx={{ width: "160px", height: "14px", bgcolor: theme.colorTokens.N[30] }}
              />
            ) : completion && completion.total > 0 ? (
              `${completion.done} / ${completion.total} Workouts completed`
            ) : (
              "\u00a0"
            )}
          </Box>
        </Box>
        <PeriodNavigator
          label={formatWeekLabel(weekStartDate)}
          period="week"
          containsToday={weekStartDate === thisWeekMonday()}
          onPrevious={goToPreviousWeek}
          onNext={goToNextWeek}
          onToday={goToThisWeek}
        />
        <ActionButton
          label="Refresh data"
          icon={<RefreshIcon sx={{ color: theme.colors_ds.PRIMARY_ICON_MAIN }} />}
          iconRight={
            <CustomTooltip message={REFRESH_HINT}>
              <InfoIcon />
            </CustomTooltip>
          }
          isDisable={activeWeekStatus === "loading"}
          onClick={reload}
        />
      </Box>
      <Box sx={styles.body}>
        <Box sx={styles.week}>{renderBody()}</Box>
        {activeWeekStatus === "ready" && <DetailsPane />}
      </Box>
      <WorkoutAnalysisPanel />
    </Box>
  );
};

export default WorkoutPlanView;

const styles = {
  // Bounded to the shell's available height (96px = breadcrumb row + content
  // padding, as HistoryScreen does) so the page itself never scrolls: the
  // week's rows scroll in their own ScrollableWrapper.
  container: {
    width: "100%",
    height: "calc(100vh - 96px)",
    minHeight: 0,
    display: "flex",
    flexDirection: "column" as "column",
    gap: "24px",
    overflow: "hidden",
  },
  // Title left, the week navigator in the middle, the refresh action right.
  // Equal side columns keep the navigator centred on the page whatever the
  // width of the title group (skeleton vs count) or of the refresh button.
  headerRow: {
    flexShrink: 0,
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
    alignItems: "center",
    gap: "24px",
    "& > :last-child": { justifySelf: "end" },
  },
  titleGroup: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column" as "column",
    gap: "2px",
  },
  title: (theme: Theme) => ({
    whiteSpace: "nowrap" as "nowrap",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "20px",
    lineHeight: "28px",
  }),
  completion: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    height: "20px",
    whiteSpace: "nowrap" as "nowrap",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
  // Two columns: the week takes what is left, the details pane a fixed
  // share so long exercise lists read comfortably without starving the days.
  body: {
    flex: 1,
    minHeight: 0,
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(360px, 44%)",
    gap: "24px",
  },
  week: {
    minHeight: 0,
    display: "flex",
    flexDirection: "column" as "column",
  },
};
